import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Wand2, FileText, Search, Image, Eye, ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { useArticles } from "@/hooks/useArticles";
import { generateSEOSuggestions } from "@/utils/seoOptimizer";
import { ArticleImageUpload } from "./ArticleImageUpload";
import { RichTextEditor } from "./RichTextEditor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface AddArticleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STEPS = [
  { id: "content", label: "Contenu", icon: FileText },
  { id: "media", label: "Média & Catégorie", icon: Image },
  { id: "seo", label: "SEO", icon: Search },
  { id: "preview", label: "Aperçu", icon: Eye },
] as const;

type StepId = typeof STEPS[number]["id"];

export const AddArticleDialog = ({ open, onOpenChange }: AddArticleDialogProps) => {
  const { createArticle } = useArticles();
  const [currentStep, setCurrentStep] = useState<StepId>("content");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    content: "",
    image: "",
    author: "Équipe Château de Clémentine",
    category: "Conseils",
    metaTitle: "",
    metaDescription: "",
    keywords: "",
    canonicalUrl: ""
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const autoFillSEO = () => {
    if (!formData.title || !formData.content) return;
    const suggestions = generateSEOSuggestions(formData.title, formData.content, formData.category);
    setFormData(prev => ({
      ...prev,
      metaTitle: suggestions.metaTitle,
      metaDescription: suggestions.metaDescription,
      keywords: suggestions.keywords,
      canonicalUrl: suggestions.canonicalUrl
    }));
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[àáâäã]/g, 'a').replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i').replace(/[òóôöõ]/g, 'o')
      .replace(/[ùúûü]/g, 'u').replace(/[ç]/g, 'c')
      .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')
      .replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.subtitle || !formData.content) return;
    try {
      setIsSubmitting(true);
      const slug = generateSlug(formData.title);
      const readTime = Math.ceil(formData.content.split(' ').filter(w => w.length > 0).length / 200);
      await createArticle({
        title: formData.title,
        subtitle: formData.subtitle,
        content: formData.content,
        image_url: formData.image || "/default-article-image.webp",
        author: formData.author,
        category: formData.category,
        meta_title: formData.metaTitle || `${formData.title} | Château de Clémentine`,
        meta_description: formData.metaDescription || formData.subtitle,
        keywords: formData.keywords,
        canonical_url: formData.canonicalUrl || `https://chateaudeclementine.com/blog/${slug}`,
        slug,
        read_time: readTime
      });
      setFormData({ title: "", subtitle: "", content: "", image: "", author: "Équipe Château de Clémentine", category: "Conseils", metaTitle: "", metaDescription: "", keywords: "", canonicalUrl: "" });
      setCurrentStep("content");
      onOpenChange(false);
    } catch (error) {
      console.error('Erreur lors de la création de l\'article:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepIndex = STEPS.findIndex(s => s.id === currentStep);
  const canGoNext = () => {
    if (currentStep === "content") return formData.title.length > 0 && formData.subtitle.length > 0 && formData.content.length > 0;
    return true;
  };
  const goNext = () => { if (stepIndex < STEPS.length - 1) setCurrentStep(STEPS[stepIndex + 1].id); };
  const goPrev = () => { if (stepIndex > 0) setCurrentStep(STEPS[stepIndex - 1].id); };

  const wordCount = formData.content.split(' ').filter(w => w.length > 0).length;
  const readTime = Math.ceil(wordCount / 200);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <div className="px-8 pt-6 pb-4 border-b">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display">Ajouter un Nouvel Article</DialogTitle>
          </DialogHeader>
          
          {/* Stepper */}
          <div className="flex items-center gap-2 mt-6">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isDone = i < stepIndex;
              return (
                <div key={step.id} className="flex items-center gap-2">
                  {i > 0 && <div className={`h-px w-8 ${isDone ? 'bg-primary' : 'bg-border'}`} />}
                  <button
                    onClick={() => setCurrentStep(step.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all
                      ${isActive 
                        ? 'bg-primary text-primary-foreground shadow-md' 
                        : isDone 
                          ? 'bg-primary/10 text-primary' 
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                  >
                    {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    <span className="hidden sm:inline">{step.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Content area - scrollable */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {/* Step 1: Content */}
          {currentStep === "content" && (
            <div className="max-w-4xl mx-auto space-y-8">
              <div>
                <h3 className="text-lg font-semibold mb-1">Contenu de l'article</h3>
                <p className="text-sm text-muted-foreground mb-6">Rédigez le titre, le sous-titre et le corps de votre article.</p>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Titre de l'article <span className="text-destructive">*</span></Label>
                  <Input 
                    placeholder="Ex : Comment organiser une fête d'anniversaire inoubliable ?"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="text-lg h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Sous-titre <span className="text-destructive">*</span></Label>
                  <Input 
                    placeholder="Résumé court qui apparaît dans les cartes du blog"
                    value={formData.subtitle}
                    onChange={(e) => handleInputChange('subtitle', e.target.value)}
                    className="h-11"
                  />
                </div>

                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => handleInputChange('content', value)}
                  rows={18}
                />
              </div>
            </div>
          )}

          {/* Step 2: Media & Category */}
          {currentStep === "media" && (
            <div className="max-w-3xl mx-auto space-y-8">
              <div>
                <h3 className="text-lg font-semibold mb-1">Média & Catégorie</h3>
                <p className="text-sm text-muted-foreground mb-6">Ajoutez une image de couverture et choisissez la catégorie.</p>
              </div>

              <div className="space-y-8">
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Image de couverture</Label>
                  <ArticleImageUpload
                    value={formData.image}
                    onChange={(value) => handleInputChange('image', value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Format recommandé : 1200×630px (ratio 1.91:1). Si aucune image n'est ajoutée, une image par défaut sera utilisée.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold">Catégorie</Label>
                    <Select value={formData.category} onValueChange={(v) => handleInputChange('category', v)}>
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Conseils">📝 Conseils</SelectItem>
                        <SelectItem value="Sécurité">🛡️ Sécurité</SelectItem>
                        <SelectItem value="Actualités">📰 Actualités</SelectItem>
                        <SelectItem value="Guide">📖 Guide</SelectItem>
                        <SelectItem value="Événements">🎉 Événements</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-semibold">Auteur</Label>
                    <Input 
                      value={formData.author}
                      onChange={(e) => handleInputChange('author', e.target.value)}
                      className="h-11"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: SEO */}
          {currentStep === "seo" && (
            <div className="max-w-3xl mx-auto space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-1">Optimisation SEO</h3>
                  <p className="text-sm text-muted-foreground">Optimisez votre article pour les moteurs de recherche.</p>
                </div>
                <Button
                  variant="outline"
                  onClick={autoFillSEO}
                  disabled={!formData.title || !formData.content}
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Auto-compléter le SEO
                </Button>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Méta Titre</Label>
                  <Input 
                    placeholder="Titre optimisé pour Google (60 caractères max)"
                    value={formData.metaTitle}
                    onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                    maxLength={60}
                    className="h-11"
                  />
                  <div className="flex justify-between">
                    <p className="text-xs text-muted-foreground">Apparaît comme titre dans les résultats Google</p>
                    <Badge variant={formData.metaTitle.length > 50 ? "destructive" : "secondary"} className="text-xs">
                      {formData.metaTitle.length}/60
                    </Badge>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Méta Description</Label>
                  <Textarea 
                    placeholder="Description attractive pour les résultats de recherche (160 caractères max)"
                    rows={3}
                    value={formData.metaDescription}
                    onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                    maxLength={160}
                  />
                  <div className="flex justify-between">
                    <p className="text-xs text-muted-foreground">Apparaît sous le titre dans les résultats Google</p>
                    <Badge variant={formData.metaDescription.length > 150 ? "destructive" : "secondary"} className="text-xs">
                      {formData.metaDescription.length}/160
                    </Badge>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Mots-clés</Label>
                  <Input 
                    placeholder="château gonflable, fête enfant, anniversaire"
                    value={formData.keywords}
                    onChange={(e) => handleInputChange('keywords', e.target.value)}
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground">Séparez les mots-clés par des virgules</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">URL Canonique</Label>
                  <Input 
                    placeholder="https://chateaudeclementine.com/blog/mon-article"
                    value={formData.canonicalUrl}
                    onChange={(e) => handleInputChange('canonicalUrl', e.target.value)}
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground">Sera auto-générée si laissée vide</p>
                </div>

                {/* Google Preview */}
                {(formData.metaTitle || formData.title) && (
                  <Card className="border-dashed">
                    <CardContent className="pt-6">
                      <p className="text-xs font-medium text-muted-foreground mb-3">Aperçu Google</p>
                      <div className="space-y-1">
                        <h3 className="text-[#1a0dab] text-base font-normal cursor-pointer hover:underline leading-tight">
                          {formData.metaTitle || formData.title}
                        </h3>
                        <p className="text-[#006621] text-xs">
                          chateaudeclementine.com › blog › {generateSlug(formData.title)}
                        </p>
                        <p className="text-xs text-[#545454] leading-relaxed">
                          {formData.metaDescription || formData.subtitle || "Aucune description renseignée."}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* SEO Tips */}
                <Card className="bg-muted/50 border-0">
                  <CardContent className="pt-6">
                    <p className="text-sm font-semibold mb-3">💡 Conseils SEO</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-muted-foreground">
                      <div className="flex items-start gap-2">
                        <Check className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />
                        <span>Utilisez des mots-clés pertinents dans le titre</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />
                        <span>Rédigez une méta description attractive</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />
                        <span>Incluez des mots-clés locaux (Rhode-Saint-Genèse)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />
                        <span>Minimum 300 mots pour un bon référencement</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Step 4: Preview */}
          {currentStep === "preview" && (
            <div className="max-w-3xl mx-auto space-y-8">
              <div>
                <h3 className="text-lg font-semibold mb-1">Aperçu final</h3>
                <p className="text-sm text-muted-foreground mb-6">Vérifiez votre article avant de le publier.</p>
              </div>

              <Card>
                <CardContent className="pt-6 space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold text-primary">{wordCount}</p>
                      <p className="text-xs text-muted-foreground">Mots</p>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold text-primary">{readTime}</p>
                      <p className="text-xs text-muted-foreground">Min de lecture</p>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <Badge variant="secondary">{formData.category}</Badge>
                      <p className="text-xs text-muted-foreground mt-1">Catégorie</p>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm font-medium truncate">{formData.author}</p>
                      <p className="text-xs text-muted-foreground">Auteur</p>
                    </div>
                  </div>

                  {/* Cover image */}
                  {formData.image && (
                    <div className="rounded-lg overflow-hidden aspect-video">
                      <img src={formData.image} alt="Couverture" className="w-full h-full object-cover" />
                    </div>
                  )}

                  {/* Title & Subtitle */}
                  <div>
                    <h1 className="text-2xl font-display font-bold text-primary mb-2">{formData.title}</h1>
                    <p className="text-muted-foreground">{formData.subtitle}</p>
                  </div>

                  {/* Content preview (truncated) */}
                  <div 
                    className="prose prose-sm max-w-none border-t pt-6
                      prose-headings:font-display prose-headings:font-bold prose-headings:text-primary
                      prose-p:mb-4 prose-p:leading-relaxed
                      prose-strong:text-primary"
                    dangerouslySetInnerHTML={{ 
                      __html: formData.content.slice(0, 1500) + (formData.content.length > 1500 ? '...' : '')
                    }}
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t bg-background flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {currentStep === "content" && (
              <span>{wordCount} mots · {readTime} min de lecture</span>
            )}
            {currentStep !== "content" && (
              <span>Étape {stepIndex + 1} sur {STEPS.length}</span>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            {stepIndex > 0 && (
              <Button variant="outline" onClick={goPrev}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Précédent
              </Button>
            )}
            {stepIndex < STEPS.length - 1 ? (
              <Button onClick={goNext} disabled={!canGoNext()} variant="hero">
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={!formData.title || !formData.subtitle || !formData.content || isSubmitting}
                variant="hero"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                {isSubmitting ? "Publication..." : "Publier l'Article"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
